// revised version of frontend/src/components/MapWithTooltips.js

import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback, useImperativeHandle } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap, useMapEvents, CircleMarker, LayerGroup } from 'react-leaflet';
import L from 'leaflet';
import { reverseGeocode } from '../utils/geocodingApi';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { toast } from 'react-toastify';
import { useMapController } from '../hooks/useMapController';
import MapVehicleMarker from './MapVehicleMarker';
import translationService from '../services/translationService';
import { useTranslation } from 'react-i18next';
import { usePreferences } from '../contexts/PreferenceContext';
import logger from '../utils/logger';

// Helper function to shorten address
const shortenAddress = (address) => {
  if (!address) return '';
  if (address.length > 30) return address.substring(0, 30) + '...';
  return address;
};

// Set up default marker icon
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Building icon for entity/default marker
const BuildingIcon = L.divIcon({
  className: '',
  html: '<div class="building-marker-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style="height: 24px; width: 24px; color: #4b5563;"><path d="M11.584 2.376a.75.75 0 01.832 0l9 6a.75.75 0 11-.832 1.248L12 3.901 3.416 9.624a.75.75 0 01-.832-1.248l9-6z" /><path fill-rule="evenodd" d="M20.25 10.332v9.918H21a.75.75 0 010 1.5H3a.75.75 0 010-1.5h.75v-9.918a.75.75 0 01.634-.74A49.109 49.109 0 0112 9c2.59 0 5.134.202 7.616.592a.75.75 0 01.634.74zm-7.5 2.418a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75zm3-.75a.75.75 0 01.75.75v6.75a.75.75 0 01-1.5 0v-6.75a.75.75 0 01.75-.75zM9 12.75a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75z" clip-rule="evenodd" /></svg></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});


// Default coordinates
const DEFAULT_CENTER = [43.7921, -79.3552]; // Toronto, Canada

// Extracted as separate component for better memoization
const RoutePolyline = React.memo(({ positions, tripStatus }) => {
  // Only render if we have at least 2 points
  if (!positions || positions.length < 2) {
    return null;
  }

  // Get color based on trip status
  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#6B7280', // gray
      dispatched: '#EAB308', // yellow
      in_progress: '#3B82F6', // blue
      completed: '#22C55E', // green
      canceled: '#EF4444', // red
    };
    return colors[status] || '#3B82F6'; // default to blue
  };

  return (
    <Polyline
      positions={positions}
      color={getStatusColor(tripStatus)}
      weight={3}
      opacity={1}
    />
  );
});
RoutePolyline.displayName = 'RoutePolyline';

const hasExplicitRiderMarkerFlags = (waypoint = {}) => (
  Object.prototype.hasOwnProperty.call(waypoint, 'is_user_pickup')
  || Object.prototype.hasOwnProperty.call(waypoint, 'is_user_dropoff')
);

const isPickupWaypoint = (waypoint = {}, preferRiderMarkerFlags = false) => {
  if (preferRiderMarkerFlags && hasExplicitRiderMarkerFlags(waypoint)) {
    return waypoint.is_user_pickup === true;
  }

  return waypoint.is_user_pickup === true || waypoint.can_pickup || waypoint.type === 'pickup';
};

const isDropoffWaypoint = (waypoint = {}, preferRiderMarkerFlags = false) => {
  if (preferRiderMarkerFlags && hasExplicitRiderMarkerFlags(waypoint)) {
    return waypoint.is_user_dropoff === true;
  }

  return waypoint.is_user_dropoff === true || waypoint.can_dropoff || waypoint.type === 'dropoff';
};

// Waypoint layer as a memoized component
const WaypointLayer = React.memo(({ waypoints, showTooltips, t, isDriver }) => {
  if (!waypoints || !waypoints.length) {
    return null;
  }
  const preferRiderMarkerFlags = !isDriver;

  // Group waypoints by location to handle overlapping points
  const waypointGroups = waypoints.reduce((groups, waypoint, idx) => {
    if (!waypoint.latitude || !waypoint.longitude) return groups;
    
    const key = `${waypoint.latitude},${waypoint.longitude}`;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push({ waypoint, idx });
    return groups;
  }, {});

  return (
    <LayerGroup>
      {Object.entries(waypointGroups).map(([locationKey, group]) => {
        const position = [Number(group[0].waypoint.latitude), Number(group[0].waypoint.longitude)];
        const hasMultiple = group.length > 1;
        
        // For single waypoints, render only CircleMarker (no icon labels)
        if (!hasMultiple) {
          const { waypoint, idx } = group[0];
          const isStart = idx === 0;
          const isEnd = idx === waypoints.length - 1;
          const isPickup = isPickupWaypoint(waypoint, preferRiderMarkerFlags);
          const isDropoff = isDropoffWaypoint(waypoint, preferRiderMarkerFlags);
          const isStop = waypoint.point_type === 'stop' && !isPickup && !isDropoff;

          return (
            <CircleMarker
              key={`waypoint-${waypoint.id || idx}-${showTooltips ? 'show' : 'hide'}`}
              center={position}
              radius={5}
              pane="markerPane"
              fillColor={
                isStart
                  ? '#0EA5E9' // blue
                  : isEnd
                  ? '#F43F5E' // red
                  : isPickup && isDropoff
                  ? '#6366F1' // purple
                  : isPickup
                  ? '#14B8A6' // teal
                  : isDropoff
                  ? '#F59E0B' // yellow
                  : '#8B5CF6' // purple
              }
              color='#ffffff'
              weight={1.5}
              opacity={1}
              fillOpacity={1}
            >
              <Tooltip
                direction={isEnd ? 'bottom' : 'top'}
                offset={isEnd ? [0, 8] : [0, -8]}
                opacity={isStart || isEnd ? 0.8 : 0.7}
                permanent={showTooltips}
                className={isStart ? 'custom-starttooltip' : isEnd ? 'custom-endtooltip' : ''}
              >
                {isStart
                  ? `${t('start')}: `
                  : isEnd
                  ? `${t('end')}: `
                  : isStop || isPickup || isDropoff
                  ? `${t('stop')}: `
                  : `${t('waypoint')} ${idx}: `}
                {`${waypoint.name || ''}${waypoint.address ? ': ' : ''}${shortenAddress(waypoint.address || '')}`}
              </Tooltip>
            </CircleMarker>
          );
        }
        
        // For overlapping waypoints, render only CircleMarker (no icon labels)
        const waypointInfos = group.map(({ waypoint, idx }) => {
          const isStart = idx === 0;
          const isEnd = idx === waypoints.length - 1;
          const isPickup = isPickupWaypoint(waypoint, preferRiderMarkerFlags);
          const isDropoff = isDropoffWaypoint(waypoint, preferRiderMarkerFlags);
          const isStop = waypoint.point_type === 'stop' && !isPickup && !isDropoff;

          let label = '';
          if (isStart) label = t('start');
          else if (isEnd) label = t('end');
          else if (isPickup) label = t('pickup');
          else if (isDropoff) label = t('dropoff');
          else if (isStop) label = t('stop');
          else label = `${t('waypoint')} ${idx}`;

          return {
            label,
            name: waypoint.name || '',
            address: waypoint.address || '',
            isStart,
            isEnd,
            isPickup,
            isDropoff,
            waypoint
          };
        });

        // Determine color based on types present
        const hasStart = waypointInfos.some(info => info.isStart);
        const hasEnd = waypointInfos.some(info => info.isEnd);
        const hasPickup = waypointInfos.some(info => info.isPickup);
        const hasDropoff = waypointInfos.some(info => info.isDropoff);

        let primaryColor = '#8B5CF6'; // default purple
        if (hasStart && hasEnd) primaryColor = '#6366F1'; // indigo for start+end
        else if (hasStart) primaryColor = '#0EA5E9'; // blue
        else if (hasEnd) primaryColor = '#F43F5E'; // red
        else if (hasPickup && hasDropoff) primaryColor = '#6366F1'; // purple
        else if (hasPickup) primaryColor = '#14B8A6'; // teal
        else if (hasDropoff) primaryColor = '#F59E0B'; // yellow

        return (
          <CircleMarker
            key={`waypoint-group-${locationKey}-${showTooltips ? 'show' : 'hide'}`}
            center={position}
            radius={5}
            pane="markerPane"
            fillColor={primaryColor}
            color='#ffffff'
            weight={1.5}
            opacity={1}
            fillOpacity={1}
          >
            <Tooltip
              direction="top"
              offset={[0, -8]}
              opacity={0.9}
              permanent={showTooltips}
              className="multi-waypoint-tooltip"
            >
              <div>
                {waypointInfos.map((info, i) => (
                  <div key={i} style={{ marginBottom: i < waypointInfos.length - 1 ? '4px' : '0' }}>
                    <strong>{info.label}:</strong> {info.name}{info.address ? ': ' : ''}{shortenAddress(info.address)}
                  </div>
                ))}
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}
    </LayerGroup>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo - only re-render when necessary
  if (prevProps.showTooltips !== nextProps.showTooltips) return false;
  if (prevProps.isDriver !== nextProps.isDriver) return false;
  if (prevProps.waypoints.length !== nextProps.waypoints.length) return false;

  // Deep waypoint comparison would be expensive, so rely on references
  return prevProps.waypoints === nextProps.waypoints;
});
WaypointLayer.displayName = 'WaypointLayer';

// Map controller component - extracted as a proper component
const MapController = React.memo(({ 
  selectedRoute, 
  currentLocation, 
  entityCoordinates, 
  onCenterEntity, 
  onMapReady, 
  onControllerReady
}) => {
  const map = useMap();
  // Keep first-fit ownership on this mounted map so one map surface cannot
  // suppress another surface's initial route/entity centering.
  const isInitialLoadRef = useRef(true);
  
  const { centerMap } = useMapController({
    selectedRoute,
    currentLocation,
    map,
    defaultCenter: entityCoordinates,
    onCenterEntity,
    onCenterReset: () => {},
    isInitialLoadRef,
  });

  useEffect(() => {
    const controllerHandle = { centerMap };

    if (typeof onMapReady === 'function') {
      onMapReady(controllerHandle);
    }

    onControllerReady?.(controllerHandle);
    
    isInitialLoadRef.current = false;

    return () => {
      onControllerReady?.(null);
    };
  }, [centerMap, onMapReady, onControllerReady]);

  return null;
});
MapController.displayName = 'MapController';

// Main component
const MapWithTooltips = forwardRef(
  (
    {
      selectedRoute,
      mapCenter,
      showDefaultMarker,
      entityAddress,
      entityCoordinates,
      t,
      tripStatus,
      pickingMode,
      onLocationPick,
      onMapChange,
      entityChangeRef = { current: false },
      currentLocation = null,
      hasRouteReferenceLocations,
      isDriver,
      showTooltips = true,
      onMapReady,
      children,
      currentEntity,
    },
    ref
  ) => {
    const mapRef = useRef(null);
    const controllerHandleRef = useRef(null);
    const userInteracting = useRef(false);
    const [shouldShowDefaultMarker, setShouldShowDefaultMarker] = useState(showDefaultMarker);
    const [markersKey, setMarkersKey] = useState(0);
    const [translatedWaypoints, setTranslatedWaypoints] = useState(null);
    const { i18n } = useTranslation();
    const { entitySettings } = usePreferences();
    const currentEntityId = currentEntity?.entityId;
    const defaultLanguage = entitySettings?.entity_default_language || 'en';
    const multiLanguageEnabled = entitySettings?.multi_language_enabled === true;

    const handleControllerReady = useCallback((controllerHandle) => {
      controllerHandleRef.current = controllerHandle;
    }, []);

    useImperativeHandle(ref, () => ({
      centerMap: () => controllerHandleRef.current?.centerMap?.()
    }), []);
    
    // Handle waypoint translations
    useEffect(() => {
      let isActive = true;
      const sourceWaypoints = selectedRoute?.waypoints;

      const translateWaypoints = async () => {
        const shouldTranslateContent = multiLanguageEnabled && i18n.language !== defaultLanguage;
        logger.debug('[MapWithTooltips] Translation conditions', {
          multiLanguageEnabled,
          entityId: currentEntityId,
          waypointCount: sourceWaypoints?.length || 0,
          language: i18n.language,
          defaultLanguage
        });
        
        // Check if translations are needed
        if (!shouldTranslateContent || !currentEntityId || !sourceWaypoints?.length) {
          logger.debug('[MapWithTooltips] Skipping translation', {
            shouldTranslateContent,
            hasEntityId: Boolean(currentEntityId),
            waypointCount: sourceWaypoints?.length || 0
          });
          if (isActive) {
            setTranslatedWaypoints(sourceWaypoints);
          }
          return;
        }
        
        try {
          const enhancedWaypoints = await translationService.enhanceContentWithTranslations(
            sourceWaypoints,
            currentEntityId,
            'waypoint',
            i18n.language,
            ['name', 'address'],
            defaultLanguage
          );
          // Route/language changes can overlap translation requests. Only the
          // latest active effect may publish labels to the rendered map.
          if (isActive) {
            setTranslatedWaypoints(enhancedWaypoints);
          }
        } catch (error) {
          logger.error('[MapWithTooltips] Error fetching waypoint translations:', error);
          // On error, use original content
          if (isActive) {
            setTranslatedWaypoints(sourceWaypoints);
          }
        }
      };

      translateWaypoints();

      return () => {
        isActive = false;
      };
    }, [
      selectedRoute?.waypoints,
      currentEntityId,
      defaultLanguage,
      multiLanguageEnabled,
      i18n.language
    ]);

    // Force re-render of route elements when selected route or trip status changes
    useEffect(() => {
      setMarkersKey(prev => prev + 1);
    }, [selectedRoute?.id, tripStatus]);


    // Validate coordinates
    const validCenter = useMemo(() => {
      return mapCenter?.every(coord => coord !== undefined) ? mapCenter : DEFAULT_CENTER;
    }, [mapCenter]);

    // Process waypoints - memoized to avoid recalculations
    const sortedWaypoints = useMemo(() => {
      // Use translated waypoints if available, otherwise use original
      const waypointsToSort = translatedWaypoints || selectedRoute?.waypoints;
      return waypointsToSort
        ? [...waypointsToSort].sort((a, b) => a.sequence_number - b.sequence_number)
        : [];
    }, [translatedWaypoints, selectedRoute?.waypoints]);

    // Memoize polyline positions separately from waypoints
    const polylinePositions = useMemo(() => {
      return sortedWaypoints
        .filter(waypoint => waypoint.latitude && waypoint.longitude)
        .map(waypoint => [Number(waypoint.latitude), Number(waypoint.longitude)]);
    }, [sortedWaypoints]);

    // Memoize location pick handler
    const handleLocationPick = useCallback(async (lat, lng) => {
      if (!pickingMode || !onLocationPick) return;
      if (!currentEntityId) {
        logger.warn('Reverse geocoding skipped without entity context');
        toast.error(t('Failed to reverse geocode address'));
        return;
      }
      
      try {
        const result = await reverseGeocode(currentEntityId, lat, lng);
        onLocationPick(lat, lng, result.address);
      } catch (error) {
        logger.error('Reverse geocoding failed:', error);
        toast.error(t('Failed to reverse geocode address'));
      }
    }, [pickingMode, onLocationPick, t, currentEntityId]);

    // Effect for cursor styling
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      
      const mapElement = map.getContainer();
      if (pickingMode) {
        mapElement.classList.add('picking-mode');
        mapElement.classList.remove('cursor-grab');
        map.dragging.enable();
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
      } else {
        mapElement.classList.remove('picking-mode');
        mapElement.classList.add('cursor-grab');
        map.dragging.enable();
        map.boxZoom.enable();
        map.doubleClickZoom.enable();
        map.scrollWheelZoom.enable();
        map.keyboard.enable();
      }
    }, [pickingMode]);

// Map Events handler
const MapEvents = React.memo(({ pickingMode, handleLocationPick, entityChangeRef, userInteracting }) => {
  useMapEvents({
    click: (e) => {
      if (pickingMode) {
        const { lat, lng } = e.latlng;
        handleLocationPick(lat, lng);
      }
    },
    dragstart: () => {
      userInteracting.current = true;
    },
    zoomstart: () => {
      userInteracting.current = true;
    },
    moveend: () => {
      if (entityChangeRef.current) {
        entityChangeRef.current = false;
      }
    },
  });

  return null;
});
MapEvents.displayName = 'MapEvents';

    // Optimize vehicle marker rendering - prevent re-renders when only other parts change
    const vehicleMarker = useMemo(() => {
      if (!currentLocation?.lat || !currentLocation?.lng) return null;
      
      return (
        <MapVehicleMarker
          position={currentLocation}
          heading={currentLocation?.heading}
          showTooltips={showTooltips}
          t={t}
        />
      );
    }, [currentLocation, showTooltips, t]);

    // Optimize entity marker rendering
    const entityMarker = useMemo(() => {
      if (!(shouldShowDefaultMarker || showDefaultMarker) || !entityCoordinates) return null;
      
      return (
        <Marker position={entityCoordinates} icon={BuildingIcon}>          
            <Tooltip 
              // key={`entity-marker-${shouldShowDefaultMarker}`}
              permanent={showTooltips}
              direction="top" 
              offset={[0, -24]} 
              opacity={0.9}
            >
              {entityAddress}
            </Tooltip>          
        </Marker>
      );
    }, [shouldShowDefaultMarker, showDefaultMarker, entityCoordinates, showTooltips, entityAddress]);

    return (
      <div className="w-full h-full relative">
        <MapContainer
          center={validCenter}
          zoom={13}
          className="w-full h-full"
          style={{ minHeight: '300px' }}
          ref={mapRef}
          scrollWheelZoom={true}
          preferCanvas={true} // Use canvas for better performance with many markers
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapController 
            selectedRoute={selectedRoute}
            currentLocation={currentLocation}
            entityCoordinates={entityCoordinates}
            onCenterEntity={setShouldShowDefaultMarker}
            onMapReady={onMapReady}
            onControllerReady={handleControllerReady}
          />
          {children}
          
          {selectedRoute?.waypoints && (
            <React.Fragment key={markersKey}>
              {!hasRouteReferenceLocations && polylinePositions.length > 1 && (
                <RoutePolyline positions={polylinePositions} tripStatus={tripStatus} />
              )}
              <WaypointLayer
                waypoints={sortedWaypoints}
                showTooltips={showTooltips}
                t={t}
                isDriver={isDriver}
              />
            </React.Fragment>
          )}
          
          {vehicleMarker}
          {entityMarker}
          
          <MapEvents pickingMode={pickingMode} handleLocationPick={handleLocationPick} entityChangeRef={entityChangeRef} userInteracting={userInteracting} />
        </MapContainer>
      </div>
    );
  }
);
MapWithTooltips.displayName = 'MapWithTooltips';

const MemoizedMapWithTooltips = React.memo(MapWithTooltips);
MemoizedMapWithTooltips.displayName = 'MemoizedMapWithTooltips';

export default MemoizedMapWithTooltips;
